import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CaretDown } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAgentflowDoc } from './useAgentflowDoc';

interface AgentflowDocSectionProps {
  flowId: string | undefined;
  open: boolean;
}

const markdownSx = {
  fontSize: 13,
  color: 'text.primary',
  lineHeight: 1.6,
  '& h1': { fontSize: 18, fontWeight: 600, mt: 2, mb: 1 },
  '& h2': { fontSize: 15, fontWeight: 600, mt: 1.5, mb: 0.5 },
  '& h3': { fontSize: 14, fontWeight: 600, mt: 1, mb: 0.5 },
  '& p': { my: 0.5 },
  '& ul': { my: 0.5, pl: 2.5 },
  '& li': { mb: 0.25 },
  '& a': { color: 'primary.main' },
  '& hr': {
    border: 'none',
    borderTop: '1px solid',
    borderColor: 'divider',
    my: 1.5,
  },
  '& code': {
    fontFamily: 'monospace',
    fontSize: 12,
    bgcolor: 'action.hover',
    px: 0.5,
    borderRadius: '3px',
  },
};

const AgentflowDocSection = ({ flowId, open }: AgentflowDocSectionProps) => {
  const {
    md,
    isLoading,
    isSaving,
    isRegenerating,
    error,
    hasDoc,
    loadDoc,
    saveDoc,
    regenerateDoc,
  } = useAgentflowDoc(flowId);

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (!open || !flowId || hasLoaded) return;
    setHasLoaded(true);
    void loadDoc();
  }, [open, flowId, hasLoaded, loadDoc]);

  const handleRegenerate = async () => {
    const confirmed = window.confirm(
      'Regenerating will overwrite the document with freshly extracted and AI-polished content. Continue?',
    );
    if (!confirmed) return;
    setIsEditing(false);
    await regenerateDoc();
  };

  const handleEdit = () => {
    setDraft(md ?? '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    await saveDoc(draft);
    setIsEditing(false);
  };

  const renderHeader = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
        Agent Documentation
      </Typography>
      <Box
        sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
        onClick={e => e.stopPropagation()}
        onFocus={e => e.stopPropagation()}
      >
        {hasDoc &&
          (isEditing ? (
            <>
              <Button
                size='small'
                variant='text'
                color='secondary'
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                size='small'
                variant='contained'
                onClick={handleSave}
                disabled={isSaving}
                sx={{ textTransform: 'none' }}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <Button
              size='small'
              variant='text'
              onClick={handleEdit}
              disabled={isRegenerating}
              sx={{ textTransform: 'none' }}
            >
              Edit
            </Button>
          ))}
        <Button
          size='small'
          variant='outlined'
          onClick={handleRegenerate}
          disabled={isRegenerating || isSaving}
          sx={{ textTransform: 'none' }}
        >
          {isRegenerating ? 'Regenerating…' : 'Regenerate'}
        </Button>
      </Box>
    </Box>
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    if (!hasDoc) {
      return (
        <Stack spacing={2} alignItems='flex-start'>
          <Typography sx={{ fontSize: 13, color: '#475467' }}>
            No agent documentation yet. Generate it from this agent's nodes and
            existing summary.
          </Typography>
          <Button
            variant='contained'
            size='small'
            onClick={handleRegenerate}
            disabled={isRegenerating}
            sx={{ textTransform: 'none' }}
          >
            {isRegenerating ? 'Generating…' : 'Generate'}
          </Button>
        </Stack>
      );
    }
    if (isEditing) {
      return (
        <TextField
          value={draft}
          onChange={e => setDraft(e.target.value)}
          multiline
          rows={20}
          fullWidth
          disabled={isSaving}
          InputProps={{
            sx: { fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5 },
          }}
        />
      );
    }
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '4px',
          p: 2,
          maxHeight: 420,
          overflow: 'auto',
          ...markdownSx,
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{md ?? ''}</ReactMarkdown>
      </Box>
    );
  };

  return (
    <Accordion
      disableGutters
      sx={{
        border: '1px solid #EAECF0',
        borderRadius: '4px',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<CaretDown size={18} />}>
        {renderHeader()}
      </AccordionSummary>
      <AccordionDetails>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {renderBody()}
      </AccordionDetails>
    </Accordion>
  );
};

export default AgentflowDocSection;
